const PENDO_FUNCTIONS = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'] as const

type PendoQueueEntry = [string, ...Array<unknown>]
type PendoQueue = Array<PendoQueueEntry>
type PendoStub = {
  _q: PendoQueue
} & Record<(typeof PENDO_FUNCTIONS)[number], (...args: Array<unknown>) => void>

const loaders: Record<string, Promise<void>> = {}

export const DEFAULT_PENDO_APP_KEY = '0738fff2-8788-48d3-5d1b-31a18a054f98'
export const CHROMELESS_PENDO_APP_KEY = '64d6919a-52d1-441a-41a1-4e2c4cccb498'

function ensurePendoStub() {
  const windowWithPendo = window as typeof window & {pendo?: PendoStub}

  if (windowWithPendo.pendo !== undefined) {
    return
  }

  const queue: PendoQueue = []
  const stub: PendoStub = {
    _q: queue
  } as PendoStub

  PENDO_FUNCTIONS.forEach(method => {
    stub[method] = (...args: Array<unknown>) => {
      const queueMethod: 'unshift' | 'push' = method === 'initialize' ? 'unshift' : 'push'
      queue[queueMethod]([method, ...args])
    }
  })

  windowWithPendo.pendo = stub
}

export function loadPendoAgent(appKey: string): Promise<void> {
  if (loaders[appKey] !== undefined) {
    return loaders[appKey]
  }

  ensurePendoStub()

  loaders[appKey] = new Promise((resolve, reject) => {
    const scriptSelector = `script[data-pendo-key="${appKey}"]`
    const existingScript = document.querySelector<HTMLScriptElement>(scriptSelector)

    if (existingScript?.dataset.loaded === 'true') {
      resolve()
      return
    }

    const script = existingScript ?? document.createElement('script')
    script.async = true
    script.dataset.pendoKey = appKey
    script.src = `https://cdn.pendo.io/agent/static/${appKey}/pendo.js`

    script.onload = () => {
      script.dataset.loaded = 'true'
      resolve()
    }
    script.onerror = () => {
      delete loaders[appKey]
      reject(new Error(`Failed to load Pendo agent for key ${appKey}`))
    }

    if (existingScript === null) {
      const head = document.querySelector<HTMLHeadElement>('head')
      if (head === null) {
        delete loaders[appKey]
        reject(new Error('Unable to locate document head to load Pendo agent'))
        return
      }
      head.appendChild(script)
    }
  })

  return loaders[appKey]
}
